// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { useDesktopApp } from '@/hooks/use-desktop-app';
import {
  Save,
  Upload,
  Building,
  Globe,
  Clock,
  Trash2,
  AlertTriangle,
  Loader2,
  Info,
} from 'lucide-react';
import { useWorkspace, type Company } from '@/contexts/context';
import { supabase } from '@saas-boilerplate/shared';

// List of public email providers where domain-based workspace joining should be disabled
const PUBLIC_EMAIL_PROVIDERS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'protonmail.com',
  'proton.me',
  'aol.com',
  'mail.com',
  'live.com',
  'msn.com',
  'ymail.com',
  'rocketmail.com',
  'me.com',
  'mac.com',
  'zoho.com',
  'gmx.com',
  'fastmail.com',
];

const isPublicEmailProvider = (domain: string): boolean => {
  return PUBLIC_EMAIL_PROVIDERS.includes(domain.toLowerCase());
};

const SHOW_PERFORMANCE_SECTION = false;
const SHOW_WORKSPACE_STATUS = false;

export function GeneralSettings() {
  const { workspace, company, userRole, refreshWorkspace, refreshCompany } = useWorkspace();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [autoSave, setAutoSave] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showFinalDeleteDialog, setShowFinalDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeleteWorkspaceDialog, setShowDeleteWorkspaceDialog] = useState(false);
  const [showDeleteWorkspaceConfirmDialog, setShowDeleteWorkspaceConfirmDialog] = useState(false);
  const [deleteWorkspaceConfirmation, setDeleteWorkspaceConfirmation] = useState('');
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Workspace fields
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceDomain, setWorkspaceDomain] = useState('');
  const [workspaceDescription, setWorkspaceDescription] = useState('');
  const [companyContext, setCompanyContext] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isPublicEmail, setIsPublicEmail] = useState(false);

  // Workspace profile / logo
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Company details (from companies table)
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('technology');
  const [companySize, setCompanySize] = useState('1-50');
  const [isSavingCompany, setIsSavingCompany] = useState(false);

  // Bot settings
  const [botName, setBotName] = useState('');

  // Localization / regional
  const [language, setLanguage] = useState('en');
  const [currency, setCurrency] = useState('usd');
  const [dateFormat, setDateFormat] = useState<'mdy' | 'dmy' | 'ymd' | 'iso'>('mdy');
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>('12h');
  const [workspaceTimezone, setWorkspaceTimezone] = useState<string>('');
  const [marketingCtaSuppressed, setMarketingCtaSuppressed] = useState(false);

  const timezoneOptions = (() => {
    const anyIntl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
    if (typeof anyIntl.supportedValuesOf === 'function') {
      try {
        return anyIntl.supportedValuesOf('timeZone');
      } catch {
        // fall through
      }
    }
    return [
      'UTC',
      'America/Los_Angeles',
      'America/Denver',
      'America/Chicago',
      'America/New_York',
      'Europe/London',
      'Europe/Paris',
      'Europe/Berlin',
      'Asia/Tokyo',
      'Asia/Singapore',
      'Australia/Sydney',
    ];
  })();

  // Track workspace ID to prevent resetting user changes on refresh
  const lastWorkspaceIdRef = useRef<string | null>(null);

  // Load workspace data and user email
  useEffect(() => {
    const loadData = async () => {
      if (workspace) {
        const isNewWorkspace = lastWorkspaceIdRef.current !== workspace.id;
        lastWorkspaceIdRef.current = workspace.id;

        // Always update these from workspace
        setWorkspaceName(workspace.name || '');
        setWorkspaceDomain(workspace.settings?.domain || '');
        setWorkspaceDescription(workspace.settings?.description || '');
        setCompanyContext(workspace.settings?.company_context || '');
        setBotName((workspace as any).bot_name || '');

        // Logo from settings or top-level column
        const existingLogo = workspace.settings?.logo_url || workspace.logo_url || null;
        setLogoUrl(existingLogo);

        // Localization & regional - use DB value if present, otherwise only set defaults on workspace change
        if (workspace.settings?.language) {
          setLanguage(workspace.settings.language);
        } else if (isNewWorkspace && typeof navigator !== 'undefined') {
          setLanguage((navigator.language || 'en').split('-')[0]);
        }

        if (workspace.settings?.currency) {
          setCurrency(workspace.settings.currency);
        } else if (isNewWorkspace) {
          setCurrency('usd');
        }

        if (workspace.settings?.date_format) {
          setDateFormat(workspace.settings.date_format);
        } else if (isNewWorkspace && typeof navigator !== 'undefined') {
          const locale = navigator.language || 'en-US';
          setDateFormat(locale === 'en-US' ? 'mdy' : 'dmy');
        }

        if (workspace.settings?.time_format) {
          setTimeFormat(workspace.settings.time_format);
        } else if (isNewWorkspace && typeof navigator !== 'undefined') {
          const locale = navigator.language || 'en-US';
          setTimeFormat(locale === 'en-US' ? '12h' : '24h');
        }

        if (workspace.settings?.timezone) {
          setWorkspaceTimezone(workspace.settings.timezone);
        } else if (isNewWorkspace) {
          setWorkspaceTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
        }

        setMarketingCtaSuppressed(Boolean((workspace as any).marketing_cta_suppressed));
      }

      // Load company details from the company object
      if (company) {
        setCompanyName(company.name || '');
        if (company.industry) setIndustry(company.industry);
        if (company.company_size) {
          const legacy = company.company_size;
          const mapped = !legacy ? '1-50'
            : ['1-10', '11-50'].includes(legacy) ? '1-50'
            : legacy === '50-200' ? '51-150'
            : ['200-1000', '1000+'].includes(legacy) ? '151+'
            : legacy;
          setCompanySize(mapped);
        }
        // companies.timezone is a legacy "utc-8"-style field, no longer surfaced.
        // The workspace IANA timezone is loaded from workspace.settings.timezone above.
      }

      // Get current user's email to check if it's a public provider
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
        const domain = user.email.split('@')[1]?.toLowerCase() || '';
        setIsPublicEmail(isPublicEmailProvider(domain));
      }
    };

    loadData();
  }, [workspace, company]);

  const canEditWorkspace = userRole === 'admin';
  const isDomainDisabled = !canEditWorkspace || isPublicEmail;

  // Data retention (workspaces.settings.retention_days). Collected at signup (30/90/180, default 90);
  // this card is its only post-onboarding home. Enforced daily on archived recordings by the
  // recording-retention-sweep cron (Phase 3 of the recording archive).
  const currentRetentionDays: number = (() => {
    const raw = (workspace?.settings as { retention_days?: unknown } | null)?.retention_days;
    const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
    return Number.isFinite(n) && n >= 30 ? n : 90;
  })();
  const [savingRetention, setSavingRetention] = useState(false);
  const applyRetention = async (days: number) => {
    if (!workspace?.id || savingRetention) return;
    setSavingRetention(true);
    try {
      const prev = (workspace.settings && typeof workspace.settings === 'object' ? workspace.settings : {}) as Record<string, unknown>;
      const { error } = await supabase
        .from('workspaces')
        .update({ settings: { ...prev, retention_days: days } })
        .eq('id', workspace.id);
      if (error) throw error;
      await refreshWorkspace({ silent: true });
      toast.success(`Data retention set to ${days} days`);
    } catch (e: any) {
      toast.error(e?.message || 'Could not save retention');
    } finally {
      setSavingRetention(false);
    }
  };

  // AI connectors (MCP server, docs/PRD_MCP_SERVER.md). Admin opt-in, default OFF: members may
  // connect the workspace's meeting record to their own AI clients (Claude, ChatGPT, Cursor).
  const aiConnectorsEnabled = Boolean((workspace as { allow_ai_connectors?: boolean } | null)?.allow_ai_connectors);
  const [savingAiConnectors, setSavingAiConnectors] = useState(false);
  const applyAiConnectors = async (next: boolean) => {
    if (!workspace?.id || savingAiConnectors) return;
    setSavingAiConnectors(true);
    try {
      const { error } = await supabase
        .from('workspaces')
        .update({ allow_ai_connectors: next })
        .eq('id', workspace.id);
      if (error) throw error;
      await refreshWorkspace({ silent: true });
      toast.success(next ? 'AI connectors enabled for this workspace' : 'AI connectors disabled');
    } catch (e: any) {
      toast.error(e?.message || 'Could not update AI connectors');
    } finally {
      setSavingAiConnectors(false);
    }
  };

  // Recording: how new meetings are captured, and whether the desktop app may record by itself.
  // See docs/PRD_DEVICE_AUTO_RECORD.md §4.3. Both live in workspaces.settings.
  const defaultCaptureMethod: 'bot' | 'desktop' =
    (workspace?.settings as { default_capture_method?: unknown } | null)?.default_capture_method === 'desktop'
      ? 'desktop'
      : 'bot';
  // Unset means allowed - the per-series "Desktop App" choice is the real gate, and an admin turns
  // this off to withdraw automatic recording across the workspace.
  const autoDesktopAllowed =
    (workspace?.settings as { allow_auto_desktop_recording?: unknown } | null)
      ?.allow_auto_desktop_recording !== false;
  const [savingCapture, setSavingCapture] = useState(false);
  const patchWorkspaceSettings = async (patch: Record<string, unknown>, successMsg: string) => {
    if (!workspace?.id || savingCapture) return;
    setSavingCapture(true);
    try {
      const prev = (workspace.settings && typeof workspace.settings === 'object' ? workspace.settings : {}) as Record<string, unknown>;
      const { error } = await supabase
        .from('workspaces')
        .update({ settings: { ...prev, ...patch } })
        .eq('id', workspace.id);
      if (error) throw error;
      await refreshWorkspace({ silent: true });
      toast.success(successMsg);
    } catch (e: any) {
      toast.error(e?.message || 'Could not save recording settings');
    } finally {
      setSavingCapture(false);
    }
  };
  const desktopApp = useDesktopApp(workspace?.id ?? null);

  // Meeting visibility (Shared vs Private). See docs/PRD_OPEN_WORKSPACES.md.
  const currentVisibility: 'shared' | 'private' = workspace?.meeting_visibility ?? 'private';
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [confirmShareOpen, setConfirmShareOpen] = useState(false);

  const applyVisibility = async (mode: 'shared' | 'private') => {
    if (!workspace?.id) return;
    setSavingVisibility(true);
    try {
      const { data, error } = await supabase.rpc('set_workspace_meeting_visibility', {
        p_workspace_id: workspace.id,
        p_mode: mode,
      });
      if (error) throw error;
      const autoPrivatized = Array.isArray(data)
        ? Number((data[0] as { auto_privatized?: number } | undefined)?.auto_privatized ?? 0)
        : Number((data as { auto_privatized?: number } | null)?.auto_privatized ?? 0);
      await refreshWorkspace();
      toast.success(
        mode === 'shared'
          ? `Workspace is now Shared${autoPrivatized ? ` - ${autoPrivatized} one-on-one ${autoPrivatized === 1 ? 'series was' : 'series were'} kept private` : ''}`
          : 'Workspace is now Private',
      );
    } catch (err: any) {
      toast.error('Failed to update meeting visibility', { description: err?.message });
    } finally {
      setSavingVisibility(false);
      setConfirmShareOpen(false);
    }
  };

  const handleSaveWorkspace = async () => {
    if (!workspace || !canEditWorkspace) return;

    setIsSaving(true);
    try {
      // Don't save domain if it's a public email provider
      const domainToSave = isPublicEmail ? '' : workspaceDomain;

      // Handle logo upload if a new file has been selected
      let newLogoUrl = logoUrl;
      if (logoFile) {
        const bucket = 'workspace-logos';
        const ext = logoFile.name.split('.').pop() || 'png';
        const path = `${workspace.id}/logo.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(path, logoFile, {
            upsert: true,
          });

        if (uploadError) {
          console.error('Error uploading logo:', uploadError);
          throw uploadError;
        }

        const { data: publicUrlData } = supabase.storage.from(bucket).getPublicUrl(path);

        newLogoUrl = publicUrlData?.publicUrl || null;
      }

      const { error } = await supabase
        .from('workspaces')
        .update({
          name: workspaceName,
          logo_url: newLogoUrl,
          workspace_domain: domainToSave || null,
          bot_name: botName.trim() || null,
          marketing_cta_suppressed: marketingCtaSuppressed,
          settings: {
            ...workspace.settings,
            domain: domainToSave,
            description: workspaceDescription,
            company_context: companyContext.trim() || null,
            language,
            currency,
            date_format: dateFormat,
            time_format: timeFormat,
            timezone: workspaceTimezone || null,
          },
        })
        .eq('id', workspace.id);

      if (error) throw error;

      await refreshWorkspace();
      setLogoFile(null);
      setLogoPreviewUrl(null);
      toast.success('Workspace settings saved!', {
        description: 'Your workspace settings have been updated',
      });
    } catch (error: any) {
      console.error('Error saving workspace:', error);
      toast.error('Failed to save settings', {
        description: error.message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCompany = async () => {
    if (!company || !canEditWorkspace) return;

    setIsSavingCompany(true);
    try {
      const { error } = await supabase
        .from('companies')
        .update({
          name: companyName,
          industry,
          company_size: companySize,
        })
        .eq('id', company.id);

      if (error) throw error;

      await refreshCompany();
      toast.success('Company details saved!', {
        description: 'Your company details have been updated',
      });
    } catch (error: any) {
      console.error('Error saving company:', error);
      toast.error('Failed to save company details', {
        description: error.message,
      });
    } finally {
      setIsSavingCompany(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1>Company Settings</h1>
          <p className="text-muted-foreground">
            Manage your company details, workspace preferences, and regional defaults
          </p>
        </div>
      </div>

      {/* Company & Workspace */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Building className="h-5 w-5" />
            <CardTitle>Company & Workspace</CardTitle>
          </div>
          <CardDescription>
            Your company name is shown in the app header and shared across your team.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo */}
          <div className="flex items-center space-x-6">
            <div className="relative">
              <Avatar className="h-20 w-20">
                <AvatarImage src={logoPreviewUrl || logoUrl || undefined} />
                <AvatarFallback className="text-lg">PR</AvatarFallback>
              </Avatar>
              <Button
                size="sm"
                variant="outline"
                className="absolute -bottom-1 -right-1"
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canEditWorkspace}
              >
                <Upload className="h-3 w-3" />
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  setLogoFile(file);
                  const url = URL.createObjectURL(file);
                  setLogoPreviewUrl(url);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Company Logo</Label>
              <p className="text-sm text-muted-foreground">
                Upload a logo (recommended: 200×200 px, max 5 MB)
              </p>
            </div>
          </div>

          {/* Company name + industry */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g. Acme Inc."
                disabled={!canEditWorkspace}
              />
              {!canEditWorkspace && (
                <p className="text-xs text-muted-foreground">Only admins can edit this field</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <Select value={industry} onValueChange={setIndustry} disabled={!canEditWorkspace}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technology">Technology</SelectItem>
                  <SelectItem value="healthcare">Healthcare</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="manufacturing">Manufacturing</SelectItem>
                  <SelectItem value="education">Education</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Company size */}
          <div className="space-y-2 md:max-w-md">
            <Label htmlFor="companySize">Company Size</Label>
            <Select value={companySize} onValueChange={setCompanySize} disabled={!canEditWorkspace}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1-50">1–50 employees</SelectItem>
                <SelectItem value="51-150">51–150 employees</SelectItem>
                <SelectItem value="151+">151+ employees</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Workspace / team name + domain */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="workspaceName">Team Name</Label>
              <Input
                id="workspaceName"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                placeholder="e.g. Product Team"
                disabled={!canEditWorkspace}
              />
              <p className="text-xs text-muted-foreground">
                Internal name for this workspace (used in settings and exports)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="workspaceDomain">Company Domain</Label>
              <Input
                id="workspaceDomain"
                value={workspaceDomain}
                onChange={(e) => setWorkspaceDomain(e.target.value)}
                placeholder="example.com"
                disabled={isDomainDisabled}
              />
              {isPublicEmail ? (
                <Alert className="mt-2">
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Domain-based auto-join is disabled for public email providers (Gmail, Yahoo,
                    Outlook, etc.). Invite members manually instead.
                  </AlertDescription>
                </Alert>
              ) : !canEditWorkspace ? (
                <p className="text-xs text-muted-foreground">Only admins can edit this field</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  New users with this email domain will automatically join your workspace
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="workspaceDescription">Description</Label>
            <Textarea
              id="workspaceDescription"
              value={workspaceDescription}
              onChange={(e) => setWorkspaceDescription(e.target.value)}
              placeholder="Brief description of your workspace…"
              rows={3}
              disabled={!canEditWorkspace}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="botName">Team default bot name</Label>
            <Input
              id="botName"
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
              placeholder="Upstand Assistant"
              disabled={!canEditWorkspace}
              maxLength={80}
            />
            <p className="text-xs text-muted-foreground">
              Fallback name used when a meeting owner has not set their own bot name in Personal settings.
              Leave blank to use “Upstand Assistant”.
            </p>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={async () => { await handleSaveCompany(); await handleSaveWorkspace(); }}
              disabled={!canEditWorkspace || isSaving || isSavingCompany || !company}
              size="sm"
            >
              {isSaving || isSavingCompany ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Company context / memory - fed into every meeting summary + Ask Upstand */}
      <Card>
        <CardHeader>
          <CardTitle>Company context</CardTitle>
          <CardDescription>
            Background about your company - products, customers, acronyms, and internal jargon. Upstand
            uses it when summarising meetings and answering in Ask Upstand, so names and terms are
            interpreted correctly.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={companyContext}
            onChange={(e) => setCompanyContext(e.target.value)}
            placeholder="e.g. Kairos and Sonare are our key products, Project TangoAlpha is the Q3 billing rebuild. Alpha corp and Globex are key customers."
            rows={5}
            disabled={!canEditWorkspace}
            className="resize-y"
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Applies to every meeting summary and Ask Upstand in this workspace.
            </p>
            <Button onClick={handleSaveWorkspace} disabled={!canEditWorkspace || isSaving} size="sm">
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data retention - the signup promise, now editable + actually enforced (recordings sweep). */}
      <Card>
        <CardHeader>
          <CardTitle>Data retention</CardTitle>
          <CardDescription>
            How long meeting recordings are kept. Applies to archived audio; transcripts and notes stay
            until you delete them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[30, 90, 180, 360].map((days) => (
              <Button
                key={days}
                type="button"
                size="sm"
                variant={currentRetentionDays === days ? 'default' : 'outline'}
                disabled={!canEditWorkspace || savingRetention}
                onClick={() => void applyRetention(days)}
              >
                {days} days
              </Button>
            ))}
          </div>
          {!canEditWorkspace && (
            <p className="mt-2 text-xs text-muted-foreground">Only workspace admins can change retention.</p>
          )}
        </CardContent>
      </Card>

      {/* Recording - default capture method + the auto-desktop kill switch. PRD_DEVICE_AUTO_RECORD */}
      <Card>
        <CardHeader>
          <CardTitle>Recording</CardTitle>
          <CardDescription>
            How meetings in this workspace get captured. Each meeting can override this in its own
            settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label className="text-sm">Default for newly enrolled meetings</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={defaultCaptureMethod === 'bot' ? 'default' : 'outline'}
                disabled={!canEditWorkspace || savingCapture}
                onClick={() => void patchWorkspaceSettings({ default_capture_method: 'bot' }, 'New meetings will use the notetaker bot')}
              >
                Notetaker bot
              </Button>
              <Button
                type="button"
                size="sm"
                variant={defaultCaptureMethod === 'desktop' ? 'default' : 'outline'}
                disabled={!canEditWorkspace || savingCapture}
                onClick={() => void patchWorkspaceSettings({ default_capture_method: 'desktop' }, 'New meetings will record from the Desktop App')}
              >
                Desktop App
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Changing this does not affect meetings that are already enrolled.
            </p>
          </div>

          <Separator />

          <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 p-3">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">Allow auto-desktop recordings</p>
              <p className="text-xs text-muted-foreground">
                Lets the Upstand desktop app start recording by itself when a meeting set to Desktop
                App begins. The person recording always sees a countdown they can cancel, and can
                pause or stop at any time. Turn this off to require a manual click every time.
              </p>
            </div>
            <Switch
              checked={autoDesktopAllowed}
              disabled={!canEditWorkspace || savingCapture}
              onCheckedChange={(v) =>
                void patchWorkspaceSettings(
                  { allow_auto_desktop_recording: Boolean(v) },
                  v ? 'Automatic desktop recording allowed' : 'Automatic desktop recording turned off',
                )
              }
            />
          </div>

          {desktopApp.devices.length > 0 ? (
            <div className="space-y-1.5">
              <Label className="text-sm">Desktop devices</Label>
              {desktopApp.devices.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{d.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {d.platform === 'win32' ? 'Windows' : d.platform === 'macos' ? 'macOS' : d.platform}
                      {d.app_version ? ` · v${d.app_version}` : ''} · last seen{' '}
                      {new Date(d.last_seen_at).toLocaleString()}
                    </p>
                  </div>
                  {d.disabled_at ? (
                    <Badge variant="destructive" className="shrink-0">Disabled</Badge>
                  ) : (
                    <Badge variant="secondary" className="shrink-0">Registered</Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              No desktop devices registered yet.{' '}
              <a href="/download" className="underline underline-offset-2 hover:text-foreground">
                Get the Mac app
              </a>
            </p>
          )}

          {!canEditWorkspace && (
            <p className="text-xs text-muted-foreground">Only workspace admins can change this.</p>
          )}
        </CardContent>
      </Card>

      {/* MCP server - admin opt-in, off by default. See docs/PRD_MCP_SERVER.md */}
      <Card>
        <CardHeader>
          <CardTitle>MCP</CardTitle>
          <CardDescription>
            MCP (Model Context Protocol) is how AI connectors like Claude, ChatGPT and Cursor plug into
            this workspace, so members can ask their own AI tools about meetings, decisions and action
            items. Each member only ever sees what they can already see in Upstand.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 p-3">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">Allow MCP connections</p>
              <p className="text-xs text-muted-foreground">
                Off by default. When on, meeting data flows to whichever AI connector a member sets up,
                under their own account and permissions.
              </p>
            </div>
            <Switch
              checked={aiConnectorsEnabled}
              disabled={!canEditWorkspace || savingAiConnectors}
              onCheckedChange={(v) => void applyAiConnectors(Boolean(v))}
            />
          </div>
          {aiConnectorsEnabled && (
            <p className="text-xs text-muted-foreground">
              Members connect from their own Settings, under Security &amp; Privacy, MCP.
            </p>
          )}
          {!canEditWorkspace && (
            <p className="text-xs text-muted-foreground">Only workspace admins can change this.</p>
          )}
        </CardContent>
      </Card>

      {/* Meeting visibility - Shared vs Private (workspace-wide). See docs/PRD_OPEN_WORKSPACES.md */}
      <Card>
        <CardHeader>
          <CardTitle>Meeting visibility</CardTitle>
          <CardDescription>
            Who can see meetings in this workspace. Individual meetings can still be made private.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <button
            type="button"
            disabled={!canEditWorkspace || savingVisibility}
            onClick={() => {
              if (currentVisibility !== 'shared') setConfirmShareOpen(true);
            }}
            className={`flex w-full items-start justify-between gap-3 rounded-lg border p-3 text-left transition-colors ${
              currentVisibility === 'shared' ? 'border-primary/50 bg-primary/5' : 'border-border/60 hover:border-border'
            } ${!canEditWorkspace || savingVisibility ? 'cursor-not-allowed opacity-70' : ''}`}
          >
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Shared</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Every active member can see all meetings (except ones marked private). Managers can
                search and ask across the team.
              </p>
            </div>
            {currentVisibility === 'shared' && <Badge variant="secondary" className="shrink-0">Current</Badge>}
          </button>

          <button
            type="button"
            disabled={!canEditWorkspace || savingVisibility}
            onClick={() => {
              if (currentVisibility !== 'private') void applyVisibility('private');
            }}
            className={`flex w-full items-start justify-between gap-3 rounded-lg border p-3 text-left transition-colors ${
              currentVisibility === 'private' ? 'border-primary/50 bg-primary/5' : 'border-border/60 hover:border-border'
            } ${!canEditWorkspace || savingVisibility ? 'cursor-not-allowed opacity-70' : ''}`}
          >
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Private</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Meetings are visible only to their owner and attendees.
              </p>
            </div>
            {currentVisibility === 'private' && <Badge variant="secondary" className="shrink-0">Current</Badge>}
          </button>

          {savingVisibility && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating…
            </p>
          )}
          {!canEditWorkspace && (
            <p className="text-xs text-muted-foreground">Only workspace admins can change this.</p>
          )}
        </CardContent>
      </Card>

      {/* Confirm Private → Shared: retroactive exposure + auto-private 1-1s. */}
      <AlertDialog open={confirmShareOpen} onOpenChange={(o) => !o && setConfirmShareOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              Make this workspace Shared?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Every active member will be able to see <span className="font-medium">all meetings in this
              workspace - past and future</span> - including transcripts, notes, decisions, and action
              items, unless a meeting is marked private. Existing one-on-one meetings are kept private
              automatically. You can switch back to Private at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingVisibility}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void applyVisibility('shared')} disabled={savingVisibility}>
              {savingVisibility ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              Make Shared
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Localization & Regional */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Globe className="h-5 w-5" />
            <CardTitle>Localization & Regional</CardTitle>
          </div>
          <CardDescription>Language, currency, and regional preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="workspaceTimezone">Default Timezone</Label>
            <Select
              value={workspaceTimezone}
              onValueChange={setWorkspaceTimezone}
              disabled={!canEditWorkspace}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a timezone" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {timezoneOptions.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Workspace default for displaying meeting times. Members can override this in their
              Personal settings.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="language">Display Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="de">Deutsch</SelectItem>
                  <SelectItem value="ja">日本語</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Default Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="usd">USD ($)</SelectItem>
                  <SelectItem value="eur">EUR (€)</SelectItem>
                  <SelectItem value="gbp">GBP (£)</SelectItem>
                  <SelectItem value="jpy">JPY (¥)</SelectItem>
                  <SelectItem value="cad">CAD (C$)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dateFormat">Date Format</Label>
              <Select value={dateFormat} onValueChange={setDateFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mdy">MM/DD/YYYY</SelectItem>
                  <SelectItem value="dmy">DD/MM/YYYY</SelectItem>
                  <SelectItem value="ymd">YYYY-MM-DD</SelectItem>
                  <SelectItem value="iso">ISO 8601</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="timeFormat">Time Format</Label>
              <Select value={timeFormat} onValueChange={setTimeFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12h">12-hour (AM/PM)</SelectItem>
                  <SelectItem value="24h">24-hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSaveWorkspace} disabled={!canEditWorkspace || isSaving} size="sm">
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Outbound messaging - admin policy that overrides every personal preference */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Info className="h-5 w-5" />
            <CardTitle>Outbound messaging</CardTitle>
          </div>
          <CardDescription>
            Workspace-wide policies applied to every outbound notification.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="cta-suppressed">Suppress the marketing CTA on every message</Label>
              <p className="text-xs text-muted-foreground">
                When on, the “try upstand.ai” line in the footer is removed from every email and
                Slack message this workspace sends - overriding any personal preference. For
                brand-conservative organisations.
              </p>
            </div>
            <Switch
              id="cta-suppressed"
              checked={marketingCtaSuppressed}
              onCheckedChange={setMarketingCtaSuppressed}
              disabled={!canEditWorkspace}
              className="mt-0.5"
            />
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSaveWorkspace} disabled={!canEditWorkspace || isSaving} size="sm">
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Performance & Storage (currently disabled) */}
      {SHOW_PERFORMANCE_SECTION && (
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <CardTitle>Performance & Storage</CardTitle>
            </div>
            <CardDescription>Optimize performance and manage data retention</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-save Changes</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically save form changes without manual submission
                </p>
              </div>
              <Switch checked={autoSave} onCheckedChange={setAutoSave} />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Real-time Updates</Label>
                <p className="text-sm text-muted-foreground">Show live updates from team members</p>
              </div>
              <Switch defaultChecked />
            </div>

            <div className="space-y-2">
              <Label>Storage Usage</Label>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Files & Attachments</span>
                  <span>2.4 GB of 10 GB</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full" style={{ width: '24%' }}></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workspace Status (currently hidden) */}
      {SHOW_WORKSPACE_STATUS && (
        <Card>
          <CardHeader>
            <CardTitle>Workspace Status</CardTitle>
            <CardDescription>Current status and health of your workspace</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold text-green-600">Healthy</div>
                <div className="text-sm text-muted-foreground">System Status</div>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold">99.9%</div>
                <div className="text-sm text-muted-foreground">Uptime (30 days)</div>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold">45ms</div>
                <div className="text-sm text-muted-foreground">Avg Response Time</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Close account - closes the company's (single) workspace for everyone. Soft delete:
          data is retained server-side and support can restore, so the copy must not claim
          irreversible destruction. */}
      <Card className="border-destructive/20">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">Close account</CardTitle>
          </div>
          <CardDescription>
            Close your company&apos;s workspace on upstand.ai for all members.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <h4 className="font-medium text-destructive mb-2">
                What happens when you close your account:
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• The workspace closes immediately and all members lose access</li>
                <li>• Every member is notified by email</li>
                <li>• Meeting capture, recaps, and integrations stop</li>
                <li>• Your data is retained for a grace period - contact support to restore it, or to have it permanently erased sooner</li>
                <li>• Your sign-in remains, but it will no longer have a workspace</li>
              </ul>
            </div>

            {!canEditWorkspace && (
              <p className="text-xs text-muted-foreground">
                Only workspace admins can close the account.
              </p>
            )}
            <AlertDialog open={showDeleteWorkspaceDialog} onOpenChange={setShowDeleteWorkspaceDialog}>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full" disabled={!canEditWorkspace}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Close account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Close your account?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This closes the workspace &quot;{workspace?.name}&quot; for every member, immediately.
                    All members will be notified by email. If you change your mind, contact support to
                    restore it during the grace period.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => {
                      setShowDeleteWorkspaceDialog(false);
                      setShowDeleteWorkspaceConfirmDialog(true);
                    }}
                  >
                    Continue
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
              open={showDeleteWorkspaceConfirmDialog}
              onOpenChange={(open) => {
                setShowDeleteWorkspaceConfirmDialog(open);
                if (!open) setDeleteWorkspaceConfirmation('');
              }}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Final confirmation</AlertDialogTitle>
                  <AlertDialogDescription>
                    Type the workspace name <strong>{workspace?.name}</strong> below to close the account.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-4">
                  <Input
                    placeholder={workspace?.name || 'Workspace name'}
                    value={deleteWorkspaceConfirmation}
                    onChange={(e) => setDeleteWorkspaceConfirmation(e.target.value)}
                    className="w-full"
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setDeleteWorkspaceConfirmation('')}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={
                      !workspace?.name ||
                      deleteWorkspaceConfirmation.trim() !== workspace.name.trim()
                    }
                    onClick={async () => {
                      if (!workspace) return;
                      if (deleteWorkspaceConfirmation.trim() !== workspace.name.trim()) return;

                      setIsDeletingWorkspace(true);
                      try {
                        const { error } = await supabase.functions.invoke('workspace-delete', {
                          body: { workspace_id: workspace.id },
                        });

                        if (error) {
                          toast.error('Failed to close account', {
                            description: error.message || 'An unexpected error occurred',
                          });
                          return;
                        }

                        toast.success('Account closed', {
                          description: 'All members have been notified.',
                        });
                        setShowDeleteWorkspaceConfirmDialog(false);
                        setDeleteWorkspaceConfirmation('');
                        window.location.href = '/';
                      } catch (err: any) {
                        toast.error('Failed to close account', {
                          description: err.message || 'An unexpected error occurred',
                        });
                      } finally {
                        setIsDeletingWorkspace(false);
                      }
                    }}
                  >
                    {isDeletingWorkspace ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Closing…
                      </>
                    ) : (
                      'Close account'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Individual save buttons are on each card above */}
    </div>
  );
}

